import { useState } from "react";
import { Check, Copy, KeyRound, Laptop, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MachineList } from "@/components/machines/machine-list";
import { usePageTitle } from "@/hooks/use-page-title";
import { useCreateToken } from "@/hooks/queries/use-tokens";
import { cn } from "@/lib/utils";

type Os = "unix" | "windows";

function detectOs(): Os {
  return typeof navigator !== "undefined" && /Win/i.test(navigator.userAgent) ? "windows" : "unix";
}

/** A one-line command with a copy button. */
function CommandLine({ command, label }: { command: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2">
      <code className="flex-1 overflow-x-auto whitespace-nowrap text-xs" data-testid={`command-${label}`}>
        {command}
      </code>
      <Button
        variant="ghost"
        size="sm"
        aria-label={`Copy ${label} command`}
        onClick={() => {
          void navigator.clipboard.writeText(command).then(() => {
            setCopied(true);
            setTimeout(() => {
              setCopied(false);
            }, 1500);
          });
        }}
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      </Button>
    </div>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <li className="flex gap-4">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/15 text-sm font-medium text-primary">
        {n}
      </span>
      <div className="min-w-0 flex-1 space-y-3">
        <h2 className="font-medium">{title}</h2>
        {children}
      </div>
    </li>
  );
}

/**
 * "Connect your computer" (/connect): install the CompBox CLI, sign it in,
 * and pick the AI it uses (the person's own Claude subscription or API key,
 * which never leaves their computer). Once connected, CompBox can send work
 * to the computer ("Run on your machine").
 */
export function ConnectComputerPage() {
  usePageTitle("Connect your computer");
  const [os, setOs] = useState<Os>(detectOs);
  const createToken = useCreateToken();
  const origin = window.location.origin;

  const install =
    os === "windows"
      ? `irm ${origin}/install.ps1 | iex`
      : `curl -fsSL ${origin}/install.sh | sh`;
  const signIn = createToken.data
    ? `compbox login --url ${origin} --api-token ${createToken.data.token}`
    : null;
  const tokenError = createToken.error instanceof Error ? createToken.error.message : null;

  return (
    <div className="mx-auto max-w-3xl space-y-8 px-4 pt-4 md:px-0">
      <div className="space-y-2">
        <h1 className="flex items-center gap-2 text-xl font-semibold">
          <Laptop className="h-5 w-5 text-primary" /> Connect your computer
        </h1>
        <p className="text-sm text-muted-foreground">
          CompBox sends AI work to your own computer, where it runs with your own AI account — your
          Claude subscription or API key. Your keys and logins stay on your computer; CompBox never
          sees them.
        </p>
      </div>

      <div role="tablist" aria-label="Operating system" className="inline-flex rounded-lg border border-border p-1">
        {(
          [
            ["unix", "macOS / Linux"],
            ["windows", "Windows"],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={os === value}
            onClick={() => {
              setOs(value);
            }}
            className={cn(
              "rounded-md px-4 py-1.5 text-sm",
              os === value ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
            )}
          >
            {label}
          </button>
        ))}
      </div>

      <ol className="space-y-8">
        <Step n={1} title="Install Node.js and Claude Code">
          <p className="text-sm text-muted-foreground">
            CompBox needs <strong>Node.js 20 or newer</strong>. Check in{" "}
            {os === "windows" ? "PowerShell" : "Terminal"}:
          </p>
          <CommandLine command="node --version" label="node-check" />
          <p className="text-sm text-muted-foreground">
            If it says &ldquo;not found&rdquo; or shows a version below 20, install the <strong>LTS</strong> version
            from{" "}
            <a className="underline underline-offset-4" href="https://nodejs.org" target="_blank" rel="noreferrer">
              nodejs.org
            </a>
            , then close and reopen {os === "windows" ? "PowerShell" : "Terminal"}. The AI itself runs through{" "}
            <a
              className="underline underline-offset-4"
              href="https://code.claude.com"
              target="_blank"
              rel="noreferrer"
            >
              Claude Code
            </a>
            : install it and sign in with your Claude account.
          </p>
        </Step>

        <Step n={2} title="Install the CompBox CLI">
          <p className="text-sm text-muted-foreground">Run:</p>
          <CommandLine command={install} label="install" />
          {os === "unix" && (
            <p className="text-xs text-muted-foreground">
              If it asks you to add a folder to your PATH, run the line it prints, then open a new Terminal window.
            </p>
          )}
        </Step>

        <Step n={3} title="Sign in from your computer">
          {signIn ? (
            <>
              <CommandLine command={signIn} label="sign-in" />
              <p className="text-xs text-muted-foreground">
                This command contains a personal key for your account and is shown only once. Don&apos;t share
                it. You can remove the key any time under Settings → API Keys.
              </p>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                Create a one-time sign-in command for this computer.
              </p>
              <Button
                variant="outline"
                disabled={createToken.isPending}
                onClick={() => {
                  createToken.mutate({ name: `Computer sign-in ${new Date().toISOString().slice(0, 10)}` });
                }}
              >
                {createToken.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <KeyRound className="mr-2 h-4 w-4" />
                )}
                Create sign-in command
              </Button>
              {tokenError && (
                <p className="text-sm text-red-400" role="alert">
                  {tokenError}
                </p>
              )}
            </>
          )}
        </Step>

        <Step n={4} title="Choose your AI and connect">
          <CommandLine command="compbox configure" label="configure" />
          <p className="text-sm text-muted-foreground">
            Pick your <strong>Claude subscription</strong> (if you use Claude Code with a Claude account) or an{" "}
            <strong>Anthropic API key</strong> (saved only on this computer, never sent to CompBox). When it
            asks which project folder the AI should work in, pick a folder with your code (a git project), or
            leave it blank and add folders later with <code className="text-xs">compbox machines dirs add</code>.{" "}
            {os === "windows" ? (
              <>
                Then run <code className="text-xs">compbox service install</code> as administrator to keep CompBox
                running.
              </>
            ) : (
              "Say yes when it offers to start the Agent Runner, so it keeps running after restarts."
            )}
          </p>
          <p className="text-xs text-muted-foreground">
            Work you send to this computer runs without stopping to ask for approval at each step, so connect
            only computers you trust.
          </p>
        </Step>
      </ol>

      <section className="space-y-3">
        <h2 className="font-medium">Your computers</h2>
        <p className="text-sm text-muted-foreground">Your computer appears here once it&apos;s connected.</p>
        <MachineList />
      </section>
    </div>
  );
}
